import { useState, useMemo } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { LineChart, Line, ResponsiveContainer } from 'recharts'
import type { MetricSnapshot, MetricSummary } from '../../types'

interface MetricsTableProps {
  timeSeries: MetricSnapshot[]
  summaries: Record<string, MetricSummary>
  availableMetrics: string[]
  loading?: boolean
}

function formatValue(value: number | null, metricName: string): string {
  if (value === null || value === undefined) return '-'

  // Determine format based on metric name
  if (metricName.includes('percent') || metricName.includes('_pct')) {
    return `${value.toFixed(1)}%`
  }
  if (metricName.includes('bytes')) {
    if (value >= 1e9) return `${(value / 1e9).toFixed(2)}GB`
    if (value >= 1e6) return `${(value / 1e6).toFixed(2)}MB`
    if (value >= 1e3) return `${(value / 1e3).toFixed(2)}KB`
    return `${value}B`
  }
  if (
    metricName.includes('duration') ||
    metricName.includes('_ms') ||
    metricName.includes('time')
  ) {
    if (value >= 1000) return `${(value / 1000).toFixed(2)}s`
    return `${value.toFixed(0)}ms`
  }
  if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`
  if (value >= 1e3) return `${(value / 1e3).toFixed(2)}K`
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

function getMetricDisplayName(metric: string): string {
  return metric
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace('Http', 'HTTP')
    .replace('Cpu', 'CPU')
    .replace('P50', 'p50')
    .replace('P95', 'p95')
    .replace('P99', 'p99')
}

interface MetricRowData {
  name: string
  displayName: string
  latest: number | null
  min: number | null
  max: number | null
  avg: number | null
  sparkline: { value: number }[]
}

export function MetricsTable({
  timeSeries,
  summaries,
  availableMetrics,
  loading = false,
}: MetricsTableProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const metrics: MetricRowData[] = useMemo(() => {
    return availableMetrics
      .map(metric => {
        const summary = summaries[metric]
        const sparklineData = timeSeries
          .map(snapshot => ({
            value: snapshot.metrics[metric] ?? 0,
          }))
          .slice(-15)

        return {
          name: metric,
          displayName: getMetricDisplayName(metric),
          latest: summary?.latest ?? timeSeries.at(-1)?.metrics[metric] ?? null,
          min: summary?.min ?? null,
          max: summary?.max ?? null,
          avg: summary?.avg ?? null,
          sparkline: sparklineData,
        }
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
  }, [availableMetrics, summaries, timeSeries])

  const displayedMetrics = isExpanded ? metrics : metrics.slice(0, 5)

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200 hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-500" />
          )}
          <h3 className="text-sm font-medium text-slate-900">All Metrics</h3>
          <span className="text-xs text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">
            {metrics.length}
          </span>
        </div>
        {!isExpanded && metrics.length > 5 && (
          <span className="text-xs text-slate-500">Click to expand</span>
        )}
      </button>

      {loading ? (
        <div className="h-32 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-500" />
        </div>
      ) : metrics.length === 0 ? (
        <div className="h-32 flex items-center justify-center text-slate-400 text-sm">
          No metrics available
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
                <th className="text-left px-4 py-2 font-medium">Metric</th>
                <th className="text-center px-4 py-2 font-medium w-24">Trend</th>
                <th className="text-right px-4 py-2 font-medium w-24">Latest</th>
                <th className="text-right px-4 py-2 font-medium w-24">Avg</th>
                <th className="text-right px-4 py-2 font-medium w-24">Min</th>
                <th className="text-right px-4 py-2 font-medium w-24">Max</th>
              </tr>
            </thead>
            <tbody>
              {displayedMetrics.map(metric => (
                <tr
                  key={metric.name}
                  className="border-b border-slate-50 hover:bg-slate-50 transition-colors"
                >
                  <td className="px-4 py-2">
                    <span className="text-sm text-slate-900">{metric.displayName}</span>
                  </td>
                  <td className="px-4 py-2">
                    {metric.sparkline.length > 1 && (
                      <div style={{ width: 80, height: 24, margin: '0 auto' }}>
                        <ResponsiveContainer width={80} height={24}>
                          <LineChart data={metric.sparkline}>
                            <Line
                              type="monotone"
                              dataKey="value"
                              stroke="#6366f1"
                              strokeWidth={1}
                              dot={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span className="text-sm font-medium text-slate-900">
                      {formatValue(metric.latest, metric.name)}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span className="text-sm text-slate-600">
                      {formatValue(metric.avg, metric.name)}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span className="text-sm text-slate-500">
                      {formatValue(metric.min, metric.name)}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span className="text-sm text-slate-500">
                      {formatValue(metric.max, metric.name)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Show more/less button */}
      {metrics.length > 5 && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-slate-50 transition-colors"
        >
          {isExpanded ? 'Show less' : `Show ${metrics.length - 5} more metrics`}
        </button>
      )}
    </div>
  )
}
